const SPINNER_SVG =
  '<svg class="spinner btn__spinner" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>';

const QTY_DEBOUNCE_MS = 100;
const CART_STALE_MS = 30000;

function getRoutes() {
  return (
    window.Pokestop?.routes || {
      cartAdd: '/cart/add.js',
      cartChange: '/cart/change.js',
      cart: '/cart.js',
    }
  );
}

function setButtonLoading(button, loading) {
  if (!button) return;

  if (loading) {
    button.classList.add('is-loading');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    if (!button.querySelector('.btn__spinner')) {
      const spinner = document.createElement('span');
      spinner.className = 'btn__spinner-wrap';
      spinner.innerHTML = SPINNER_SVG;
      button.appendChild(spinner);
    }
  } else {
    button.classList.remove('is-loading');
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.querySelector('.btn__spinner-wrap')?.remove();
  }
}

function pulseCartBadge() {
  const badge = document.querySelector('[data-cart-badge]');
  if (!badge) return;
  badge.classList.remove('is-updated');
  void badge.offsetWidth;
  badge.classList.add('is-updated');
}

function sanitizeHttpUrl(url) {
  if (!url || typeof url !== 'string') return '';

  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';
    return parsed.href;
  } catch {
    return '';
  }
}

function parseVariantId(variantId) {
  const id = Number.parseInt(String(variantId), 10);
  if (!Number.isFinite(id) || id <= 0) return null;
  return id;
}

function getLinePrice(item) {
  return item.final_line_price ?? item.line_price ?? item.price * item.quantity;
}

function formatMoney(cents, currency) {
  if (cents == null || Number.isNaN(Number(cents))) return '';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(Number(cents) / 100);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.freeShipping = parseFloat(this.dataset.freeShipping) || 250;
    this.giveaway = parseFloat(this.dataset.giveaway) || 250;
    this.currency = this.dataset.currency || 'EUR';
    this.cart = null;
    this.lastFetched = 0;
    this.changeTimers = new Map();
    this.lastOpener = null;
    try {
      this.i18n = JSON.parse(this.dataset.i18n || '{}');
    } catch {
      this.i18n = {};
    }
    this.bindEvents();
  }

  t(key, fallback) {
    return this.i18n?.[key] ?? fallback;
  }

  bindEvents() {
    document.querySelectorAll('[data-cart-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.lastOpener = btn;
        this.open();
      });
    });

    this.querySelectorAll('[data-cart-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });

    document.addEventListener('click', (e) => {
      const addBtn = e.target.closest('[data-add-to-cart]');
      if (!addBtn || addBtn.disabled) return;

      e.preventDefault();
      e.stopPropagation();
      this.addItem(addBtn.dataset.variantId, addBtn);
    });

    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        this.queueQuantityChange(removeBtn.dataset.lineKey, 0, removeBtn.closest('.cart-drawer__item'));
        return;
      }

      const qtyBtn = e.target.closest('[data-qty-change]');
      if (qtyBtn) {
        e.preventDefault();
        const lineKey = qtyBtn.dataset.lineKey;
        const row = qtyBtn.closest('.cart-drawer__item');
        const qtyEl = row?.querySelector('[data-qty]');
        if (!lineKey || !qtyEl) return;

        const current = parseInt(qtyEl.textContent, 10);
        const delta = parseInt(qtyBtn.dataset.qtyChange, 10);
        const nextQty = current + delta;
        if (nextQty < 0) return;

        this.queueQuantityChange(lineKey, nextQty, row);
      }
    });
  }

  findRow(lineKey) {
    return this.querySelector(`[data-line-key="${CSS.escape(lineKey)}"]`);
  }

  showError(message) {
    const el = this.querySelector('[data-cart-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(this._errorTimer);
    this._errorTimer = setTimeout(() => {
      el.hidden = true;
    }, 4000);
  }

  queueQuantityChange(lineKey, quantity, row) {
    if (!row) row = this.findRow(lineKey);
    if (!row) return;

    this.hideError();
    const qtyEl = row.querySelector('[data-qty]');
    if (qtyEl) qtyEl.textContent = quantity;
    row.classList.add('cart-drawer__item--updating');

    if (this.cart) {
      this.applyOptimisticSummary(quantity, row);
    }

    const existing = this.changeTimers.get(lineKey);
    if (existing) clearTimeout(existing.timer);

    const timer = setTimeout(() => {
      this.changeTimers.delete(lineKey);
      this.flushQuantityChange(lineKey, quantity, row);
    }, QTY_DEBOUNCE_MS);

    this.changeTimers.set(lineKey, { timer, quantity, row });
  }

  hideError() {
    const el = this.querySelector('[data-cart-error]');
    if (el) el.hidden = true;
  }

  applyOptimisticSummary(quantity, row) {
    if (!this.cart) return;

    const lineKey = row.dataset.lineKey;
    const item = this.cart.items.find((i) => i.key === lineKey);
    if (!item) return;

    const prevQty = item.quantity;
    const prevLine = getLinePrice(item);
    const diff = quantity - prevQty;

    item.quantity = quantity;

    if (quantity === 0) {
      this.cart.item_count = Math.max(0, this.cart.item_count - prevQty);
      item.final_line_price = 0;
      item.line_price = 0;
    } else {
      this.cart.item_count = Math.max(0, this.cart.item_count + diff);
      const unitPrice = item.final_price ?? item.price ?? (prevQty ? prevLine / prevQty : 0);
      item.final_line_price = unitPrice * quantity;
      item.line_price = item.final_line_price;
    }

    const priceDelta = item.final_line_price - prevLine;
    this.cart.total_price = Math.max(0, this.cart.total_price + priceDelta);

    this.updateSummary(this.cart);
  }

  async flushQuantityChange(lineKey, quantity, row) {
    if (!lineKey || typeof lineKey !== 'string') return;

    const safeQuantity = Number.parseInt(String(quantity), 10);
    if (!Number.isFinite(safeQuantity) || safeQuantity < 0 || safeQuantity > 9999) return;

    try {
      const response = await fetch(getRoutes().cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: safeQuantity }),
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();
      this.lastFetched = Date.now();
      this.currency = cart.currency;
      this.applyCart(cart, { fullRender: quantity === 0 });
    } catch (error) {
      console.error('Cart update error:', error);
      this.showError(this.t('errorUpdate', "Couldn't update your cart — please try again."));
      await this.refresh({ silent: true, forceFull: true });
    } finally {
      row?.classList.remove('cart-drawer__item--updating');
    }
  }

  open({ skipRefresh = false } = {}) {
    this.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    document.querySelector('.page-main')?.setAttribute('aria-hidden', 'true');
    document.querySelector('.site-header')?.setAttribute('aria-hidden', 'true');

    const stale = Date.now() - this.lastFetched > CART_STALE_MS;
    if (!skipRefresh && (!this.cart || stale)) {
      this.refresh({ silent: true });
    }

    this.trapFocus();
  }

  close() {
    this.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    document.querySelector('.page-main')?.removeAttribute('aria-hidden');
    document.querySelector('.site-header')?.removeAttribute('aria-hidden');
    this.releaseFocus();
    this.lastOpener?.focus();
  }

  trapFocus() {
    const panel = this.querySelector('.cart-drawer__panel');
    if (!panel) return;

    const closeBtn = this.querySelector('[data-cart-close]');
    closeBtn?.focus();

    this._focusHandler = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = panel.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled])'
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    panel.addEventListener('keydown', this._focusHandler);
  }

  releaseFocus() {
    const panel = this.querySelector('.cart-drawer__panel');
    if (panel && this._focusHandler) {
      panel.removeEventListener('keydown', this._focusHandler);
    }
  }

  async addItem(variantId, button) {
    const parsedVariantId = parseVariantId(variantId);
    if (!parsedVariantId) return;

    setButtonLoading(button, true);
    this.hideError();

    try {
      const response = await fetch(getRoutes().cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parsedVariantId, quantity: 1 }] }),
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      await this.refresh({ forceFull: true });
      this.open({ skipRefresh: true });
      pulseCartBadge();
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showError(this.t('errorAdd', "Couldn't add to cart — please try again."));
    } finally {
      setButtonLoading(button, false);
    }
  }

  async refresh({ silent = false, forceFull = false } = {}) {
    try {
      const res = await fetch(getRoutes().cart);
      const cart = await res.json();
      this.lastFetched = Date.now();
      this.currency = cart.currency;
      const fullRender = forceFull || this.needsFullRender(cart);
      this.applyCart(cart, { fullRender });
    } catch (error) {
      if (!silent) {
        console.error('Cart refresh error:', error);
        this.showError(this.t('errorLoad', "Couldn't load your cart — please try again."));
      }
    }
  }

  needsFullRender(cart) {
    if (!this.querySelector('.cart-drawer__item')) return true;

    const existing = new Set(
      [...this.querySelectorAll('.cart-drawer__item')].map((el) => el.dataset.lineKey)
    );
    const incoming = cart.items.map((i) => i.key);

    if (existing.size !== incoming.length) return true;
    return incoming.some((key) => !existing.has(key));
  }

  applyCart(cart, { fullRender = true } = {}) {
    this.cart = cart;
    this.currency = cart.currency || this.currency;
    this.updateHeaderBadge(cart.item_count);

    if (cart.item_count === 0) {
      this.renderEmpty();
      return;
    }

    this.updateSummary(cart);

    if (fullRender) {
      this.renderItems(cart);
      return;
    }

    cart.items.forEach((item) => this.updateLine(item, cart.currency));
  }

  updateSummary(cart) {
    const countLabel = this.querySelector('[data-cart-count-label]');
    const footer = this.querySelector('[data-cart-footer]');
    const progress = this.querySelector('[data-cart-progress]');

    countLabel.textContent = `${cart.item_count} ${cart.item_count !== 1 ? this.t('items', 'items') : this.t('item', 'item')}`;
    footer.hidden = false;
    progress.hidden = false;

    this.updateProgress(cart.total_price / 100, cart.currency);
    this.querySelector('[data-cart-subtotal]').textContent = formatMoney(
      cart.total_price,
      cart.currency
    );
  }

  updateLine(item, currency) {
    const row = this.findRow(item.key);
    if (!row) return;

    const qtyEl = row.querySelector('[data-qty]');
    const priceEl = row.querySelector('.cart-drawer__item-price');

    if (qtyEl) {
      qtyEl.textContent = item.quantity;
      qtyEl.setAttribute('aria-label', `${this.t('quantity', 'Quantity')}: ${item.quantity}`);
    }
    if (priceEl) priceEl.textContent = formatMoney(getLinePrice(item), currency);
    row.classList.remove('cart-drawer__item--updating');
  }

  renderEmpty() {
    const countLabel = this.querySelector('[data-cart-count-label]');
    const itemsEl = this.querySelector('[data-cart-items]');
    const footer = this.querySelector('[data-cart-footer]');
    const progress = this.querySelector('[data-cart-progress]');

    countLabel.textContent = this.t('empty', 'Your cart is empty');

    const empty = document.createElement('div');
    empty.className = 'cart-drawer__empty';
    empty.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cart-drawer__empty-icon"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>';

    const emptyText = document.createElement('p');
    emptyText.textContent = this.t('empty', 'Your cart is empty');
    empty.appendChild(emptyText);
    itemsEl.replaceChildren(empty);
    footer.hidden = true;
    progress.hidden = true;
    this.updateHeaderBadge(0);
  }

  renderItems(cart) {
    const itemsEl = this.querySelector('[data-cart-items]');
    const scrollTop = itemsEl.scrollTop;

    const list = document.createElement('div');
    list.className = 'cart-drawer__items';

    cart.items.forEach((item) => {
      const row = document.createElement('div');
      row.className = 'cart-drawer__item';
      row.dataset.lineKey = item.key;

      const imageWrap = document.createElement('div');
      imageWrap.className = 'cart-drawer__item-image';
      const imageUrl = sanitizeHttpUrl(item.image);
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = item.product_title || '';
        img.loading = 'lazy';
        img.width = 80;
        img.height = 80;
        imageWrap.appendChild(img);
      }

      const info = document.createElement('div');
      info.className = 'cart-drawer__item-info';

      const title = document.createElement('h4');
      title.textContent = item.product_title || '';

      const price = document.createElement('p');
      price.className = 'cart-drawer__item-price';
      price.textContent = formatMoney(getLinePrice(item), cart.currency);

      info.appendChild(title);
      if (item.variant_title && item.variant_title !== 'Default Title') {
        const variant = document.createElement('p');
        variant.className = 'cart-drawer__item-variant';
        variant.textContent = item.variant_title;
        info.appendChild(variant);
      }
      info.appendChild(price);

      const actions = document.createElement('div');
      actions.className = 'cart-drawer__item-actions';

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'btn btn--ghost btn--icon';
      removeBtn.style.width = '1.5rem';
      removeBtn.style.height = '1.5rem';
      removeBtn.dataset.removeItem = '';
      removeBtn.dataset.lineKey = item.key;
      removeBtn.setAttribute('aria-label', this.t('removeItem', 'Remove item'));
      removeBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>';

      const qtyWrap = document.createElement('div');
      qtyWrap.className = 'cart-drawer__qty';

      const decBtn = document.createElement('button');
      decBtn.type = 'button';
      decBtn.className = 'btn btn--outline btn--icon';
      decBtn.style.width = '1.5rem';
      decBtn.style.height = '1.5rem';
      decBtn.dataset.qtyChange = '-1';
      decBtn.dataset.lineKey = item.key;
      decBtn.setAttribute('aria-label', this.t('decreaseQuantity', 'Decrease quantity'));
      decBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>';

      const qty = document.createElement('span');
      qty.dataset.qty = '';
      qty.setAttribute('aria-live', 'polite');
      qty.setAttribute('aria-label', `${this.t('quantity', 'Quantity')}: ${item.quantity}`);
      qty.textContent = String(item.quantity);

      const incBtn = document.createElement('button');
      incBtn.type = 'button';
      incBtn.className = 'btn btn--outline btn--icon';
      incBtn.style.width = '1.5rem';
      incBtn.style.height = '1.5rem';
      incBtn.dataset.qtyChange = '1';
      incBtn.dataset.lineKey = item.key;
      incBtn.setAttribute('aria-label', this.t('increaseQuantity', 'Increase quantity'));
      incBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="M12 5v14"/></svg>';

      qtyWrap.append(decBtn, qty, incBtn);
      actions.append(removeBtn, qtyWrap);
      row.append(imageWrap, info, actions);
      list.appendChild(row);
    });

    itemsEl.replaceChildren(list);
    itemsEl.scrollTop = scrollTop;
  }

  updateProgress(total, currency) {
    const shippingPct = Math.min(100, (total / this.freeShipping) * 100);
    const giveawayPct = Math.min(100, (total / this.giveaway) * 100);
    const freeShippingReached = total >= this.freeShipping;
    const giveawayReached = total >= this.giveaway;

    const shippingLabel = this.querySelector('[data-shipping-label]');
    const giveawayLabel = this.querySelector('[data-giveaway-label]');

    const remainingShipping = Math.max(0, this.freeShipping - total);
    const remainingGiveaway = Math.max(0, this.giveaway - total);

    shippingLabel.querySelector('span').textContent = freeShippingReached
      ? this.t('freeShippingUnlocked', 'You unlocked FREE shipping!')
      : this.t('freeShippingAway', '__AMOUNT__ away from free shipping').replace('__AMOUNT__', formatMoney(remainingShipping * 100, currency));
    shippingLabel.classList.toggle('is-reached', freeShippingReached);
    this.querySelector('[data-shipping-progress]').style.width = `${shippingPct}%`;

    giveawayLabel.querySelector('span').textContent = giveawayReached
      ? this.t('giveawayUnlocked', "You're entered in the GIVEAWAY!")
      : this.t('giveawayAway', '__AMOUNT__ away from a free giveaway entry').replace('__AMOUNT__', formatMoney(remainingGiveaway * 100, currency));
    giveawayLabel.classList.toggle('is-reached', giveawayReached);
    this.querySelector('[data-giveaway-progress]').style.width = `${giveawayPct}%`;
  }

  updateHeaderBadge(count) {
    const badge = document.querySelector('[data-cart-badge]');
    if (!badge) return;
    badge.textContent = count;
    badge.hidden = count === 0;
  }
}

function initProductForm() {
  const form = document.getElementById('product-form');
  if (!form) return;

  const variantInput = document.getElementById('variant-id-input');
  const priceEl = document.getElementById('product-price');
  const imageEl = document.getElementById('product-main-image');
  const addBtn = document.getElementById('add-to-cart-btn');
  const variantBtns = form.querySelectorAll('[data-variant-id]');

  variantBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      variantBtns.forEach((b) => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      variantInput.value = btn.dataset.variantId;
      priceEl.textContent = btn.dataset.variantPrice;

      const imageUrl = sanitizeHttpUrl(btn.dataset.variantImage);
      if (imageEl && imageUrl) {
        imageEl.src = imageUrl;
        imageEl.srcset = '';
      }

      const available = btn.dataset.variantAvailable === 'true';
      addBtn.disabled = !available;
      addBtn.textContent = available
      ? (window.Pokestop?.i18n?.addToCart || 'ADD TO CART')
      : (window.Pokestop?.i18n?.soldOut || 'SOLD OUT');
    });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const variantId = parseVariantId(variantInput.value);
    const drawer = document.querySelector('cart-drawer');

    if (!variantId) return;

    setButtonLoading(addBtn, true);

    try {
      const response = await fetch(getRoutes().cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      if (drawer) {
        await drawer.refresh({ forceFull: true });
        drawer.open({ skipRefresh: true });
        pulseCartBadge();
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      drawer?.showError(drawer?.t?.('errorAdd') ?? "Couldn't add to cart — please try again.");
    } finally {
      setButtonLoading(addBtn, false);
    }
  });
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductForm);
} else {
  initProductForm();
}